import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TrinityMotif from "@/components/TrinityMotif";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, apiError, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative border-r border-border grid-lines flex-col justify-between p-12">
        <TrinityMotif className="w-full max-w-md mx-auto mt-6 drop-shadow-[0_30px_60px_rgba(58,45,184,0.25)]" />
        <div>
          <p className="overline text-[#7C3AED] mb-3">Specia AI TestAgents as a Service</p>
          <h2 className="neon-word text-4xl leading-none">TRINITY</h2>
          <p className="font-heading font-semibold text-[#1E1560] mt-4">Think. Test. Trust.</p>
          <p className="text-secondary mt-2 max-w-sm">Sign in to orchestrate your autonomous SAP testing agents.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <p className="overline text-[#7C3AED] mb-2">Customer sign in</p>
          <h1 className="font-heading font-bold text-3xl tracking-tight mb-8">Access your dashboard</h1>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs text-secondary">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email-input" className="rounded-sm bg-card border-border mt-1.5 h-11" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs text-secondary">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password-input" className="rounded-sm bg-card border-border mt-1.5 h-11" placeholder="••••••••" />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-btn"
            className="rounded-sm w-full mt-8 h-11 bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
          <p className="text-sm text-secondary mt-6 text-center">
            No account? <Link to="/register" className="text-[#3A2DB8] hover:underline font-semibold" data-testid="to-register-link">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
